<script setup lang="ts">
import { computed, onMounted, onBeforeUnmount, ref } from 'vue';
import InputSwitch from 'primevue/inputswitch';
import { useToast } from 'primevue/usetoast';
import UserCreate from '@/views/Users/components/UserCreate';
import UserListActions from './UserListActions.vue';
import ImportUsersModal from '@/components/UploadModal/ImportUsersModal.vue';
import ImportIEPModal from '@/components/UploadModal/ImportIEPModal.vue';
import ArchiveModal from '@/components/Modal/ArchiveModal';
import { PERMISSIONS } from '@/enum';
import { can, useDateTime, useRouting, isValidForRoleByTenant, useRequestCancel } from '@/composables';
import { usePeopleStore, usePeopleTypesStore, useAuthStore } from '@/store';
import { usePersonTypes } from '@/composables/usePersonTypes';
const authStore = useAuthStore();
import { PeopleService } from '@/services';
import { UserListFilterInterface, PeopleInterface } from '@/types';

const props = defineProps<{ studentsOnly?: boolean }>();

const $routing = useRouting();
const peopleStore = usePeopleStore();
const peopleTypeStore = usePeopleTypesStore();
const toast = useToast();
const requestCancel = useRequestCancel();
const importUsersModalOpen = ref(false);

const importIEPModalOpen = ref(false);

const defaultFilters = {
  archived: false,
  search: '',
  order: '',
  order_by: '',
  page: '',
  per_page: 15,
  person_type_id: '',
};

const displayAddUserModel = ref(false);

const filters = ref<UserListFilterInterface>({} as UserListFilterInterface);
const selectedUsers = ref<PeopleInterface[]>([]);

const showBulkActionBar = computed(() => {
  return selectedUsers.value.length > 0;
});
const toArchive = computed(() => selectedUsers.value.filter((u) => !u.archived_at).map((u) => u.id));
const toUnarchive = computed(() => selectedUsers.value.filter((u) => u.archived_at).map((u) => u.id));

onMounted(() => init());

onBeforeUnmount(() => {
  requestCancel.requestSaverAbortAll();
});

$routing.routeChangeWatcher(async () => {
  if (isUserList.value) {
    if (can([PERMISSIONS.PEOPLE_INDEX])) await peopleStore.getPeople(peopleQuery.value);
  }
});

const hasFilter = computed<boolean>(() => !!Object.keys($routing.route.query).length);
const peopleQuery = computed(() => ({
  ...$routing.route.query,
  ...(props.studentsOnly ? {} : { exclude_students: 1 }),
}));

const isUserList = computed<boolean>(() => $routing.route.name === 'user.list' || !!props.studentsOnly);

const init = async () => {
  if (isUserList.value) {
    defineFilters();
    if (!props.studentsOnly && can([PERMISSIONS.PEOPLE_TYPES_INDEX]))
      await peopleTypeStore.getPeopleTypes({ exclude_students: 1 });
    if (can([PERMISSIONS.PEOPLE_INDEX])) await peopleStore.getPeople(peopleQuery.value);
  }
};

const onCancelAdd = () => (importUsersModalOpen.value = false);

const onCancelIEPAdd = () => (importIEPModalOpen.value = false);

const doSuccess = (detail: string, summary: string) => {
  toast.add({
    severity: 'success',
    summary,
    detail,
    life: 5000,
  });
};

const doFail = (detail: string) => {
  toast.add({
    severity: 'error',
    summary: 'Error',
    detail: detail,
    life: 5000,
  });
};

const defineFilters = async () => {
  const query = $routing.route.query;

  filters.value = {
    archived: query.archived === '1',
    search: query.search ? (query.search as string) : '',
    page: query.page ? parseInt(query.page as string) : '',
    per_page: query.per_page ? parseInt(query.per_page as string) : defaultFilters.per_page,
    person_type_id: query.person_type_id ? parseInt(query.person_type_id as string) : '',
  };
};

const onAddUserToggle = () => (displayAddUserModel.value = !displayAddUserModel.value);
const onArchived = (event) => {
  const archived = event.target.checked;
  filters.value.archived = archived;
  $routing.addQueryParam('archived', filters.value.archived ? '1' : '0');
};

const onClearFilters = () => {
  filters.value = { ...(defaultFilters as UserListFilterInterface) };
  $routing.removeAllQueryParam();
};

const onPeopleTypeChange = () => $routing.addQueryParam('person_type_id', filters.value.person_type_id);

/**
 * Handle the filtering of results based on the provided search terms,
 * given 3 or or more characters have been provided.
 */
const onSearch = (search: string) => {
  const hasSearchTerm = search.length >= 3;

  if (hasSearchTerm) {
    $routing.addQueryParam('complex_search', search);
    filters.value.search = search;
  } else {
    $routing.removeQueryParam('complex_search');
  }
};

const onPage = (event) => {
  filters.value.per_page = event.rows;
  $routing.addQueryParams({ per_page: event.rows, page: event.page + 1 });
};

const onSort = (event) => {
  const order = event.sortOrder === 1 ? 'asc' : 'desc';
  $routing.addQueryParams({ order, order_by: event.sortField });
};

function clearSelection() {
  selectedUsers.value = [];
}
async function onBulkPatch() {
  if (!toArchive.value.length && !toUnarchive.value.length) return;
  try {
    await PeopleService.bulkPatch({ toArchive: toArchive.value, toUnarchive: toUnarchive.value }, '/bulk-patch');
    toast.add({
      severity: 'success',
      summary: 'Bulk Action Complete',
      detail: `Archived: ${toArchive.value.length}, Unarchived: ${toUnarchive.value.length}`,
      life: 5000,
    });
    selectedUsers.value = [];
    await peopleStore.getPeople(peopleQuery.value);
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Bulk Action Failed', detail: e?.message || 'Error', life: 5000 });
  }
}
</script>

<template>
  <div class="page-content">
    <Card>
      <template #content>
        <UserCreate v-if="displayAddUserModel && can([PERMISSIONS.PEOPLE_STORE])" @cancel="onAddUserToggle" />
        <div class="flex flex-wrap gap-3 my-3">
          <div class="flex align-items-center gap-3 flex-1">
            <IconField icon-position="left" class="ml-3">
              <InputIcon>
                <Icon name="search" class="text-lg" />
              </InputIcon>
              <InputText
                v-model="filters.search"
                v-debounce:600ms="onSearch"
                class="w-full"
                placeholder="Search by Name"
              />
            </IconField>
            <template v-if="!props.studentsOnly">
              <Skeleton v-if="peopleTypeStore.loading" height="36px" width="160px" border-radius="4" />
              <Dropdown
                v-else
                id="users_people_types"
                v-model="filters.person_type_id"
                type="secondary"
                :options="peopleTypeStore.types"
                option-label="title"
                option-value="id"
                placeholder="Filter by Type"
                @change="onPeopleTypeChange"
              />
            </template>
            <div v-if="can([PERMISSIONS.PEOPLE_ARCHIVE])" class="flex align-items-center">
              <InputSwitch v-model="filters.archived" class="mr-2" @change="onArchived" />
              <div class="text-color-secondary">
                <label>Show Archived</label>
              </div>
            </div>
            <div>
              <AppButton
                v-if="hasFilter"
                type="secondary"
                text="Clear Filters"
                icon-left="clear"
                @click="onClearFilters"
              />
            </div>
          </div>
          <AppButton
            v-if="can([PERMISSIONS.PEOPLE_STORE])"
            type="primary"
            text="Add User"
            icon-left="add"
            @click="onAddUserToggle"
          />
        </div>

        <div
          v-if="showBulkActionBar"
          class="bulk-action-bar flex align-items-center gap-3 mb-3 p-3 bg-gray-50 border-1 border-primary-300 border-round-md"
          role="region"
          aria-label="Bulk user actions"
          tabindex="0"
        >
          <span class="font-semibold mr-2">{{ selectedUsers.length }} selected</span>
          <AppButton
            :type="toArchive.length > 0 || toUnarchive.length > 0 ? 'primary' : 'secondary'"
            :text="
              toArchive.length > 0 && toUnarchive.length > 0
                ? `Archive (${toArchive.length}) / Unarchive (${toUnarchive.length})`
                : toArchive.length > 0
                ? `Archive (${toArchive.length})`
                : toUnarchive.length > 0
                ? `Unarchive (${toUnarchive.length})`
                : 'Archive / Unarchive'
            "
            icon-left="archive"
            aria-label="Bulk archive/unarchive selected users"
            tabindex="0"
            :disabled="toArchive.length === 0 && toUnarchive.length === 0"
            @click="onBulkPatch"
            @keyup.enter="onBulkPatch"
          />
          <AppButton
            type="secondary"
            text="Clear Selection"
            icon-left="clear"
            aria-label="Clear selection"
            tabindex="0"
            @click="clearSelection"
            @keyup.enter="clearSelection"
          />
        </div>
        <DataTable
          v-if="can([PERMISSIONS.PEOPLE_INDEX])"
          v-model:filters="filters"
          v-model:selection="selectedUsers"
          filter-display="menu"
          :value="peopleStore.people.data"
          :paginator="true"
          class="p-datatable-customers"
          :class="{ 'table-loading': peopleStore.people.loading }"
          :rows="filters.per_page"
          :total-records="peopleStore.people.total"
          :row-class="({ archived_at }) => (archived_at ? 'grey-row' : null)"
          data-key="id"
          :row-hover="true"
          :lazy="true"
          loading-icon="logo-spinner"
          :loading="peopleStore.people.loading"
          :rows-per-page-options="[15, 25, 30]"
          paginator-template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          current-page-report-template="Showing {first} to {last} of {totalRecords}"
          responsive-layout="scroll"
          @page="onPage"
          @sort="onSort"
        >
          <template #empty>
            <span v-if="!peopleStore.people.loading && peopleStore.people.data.length == 0">No users found</span>
          </template>
          <template v-if="peopleStore.people.data.length">
            <Column
              v-if="authStore.isAdmin() || usePersonTypes.isSchoolAdmin(authStore.acronymType)"
              selection-mode="multiple"
              style="width: 3em"
            />
            <Column field="archived_at">
              <template #body="{ data }">
                <Icon
                  v-if="data.archived_at"
                  v-tooltip.top="{
                    value: 'Archived',
                    escape: true,
                    fitContent: true,
                  }"
                  name="info"
                  class="mt-1 text-color-gray-dark text-base cursor-pointer"
                />
              </template>
            </Column>
            <Column field="full_name" header="Name" sortable>
              <template #body="{ data }">
                <RouterLink
                  v-if="isValidForRoleByTenant(data.people_type)"
                  :to="`/user/${data.id}`"
                  class="font-semibold"
                  v-text="data.full_name"
                />
                <span v-else class="font-semibold" v-text="data.full_name" />
              </template>
            </Column>
            <Column field="email" header="Email" :sortable="true">
              <template #body="{ data }">
                <a :href="`mailto:${data.email}`" v-text="data.email" />
              </template>
            </Column>
            <Column field="people_type.title" header="Type"></Column>
            <Column field="location" header="State">
              <template #body="{ data }">
                <span class="ml-2">{{ data.location?.state?.name ?? 'N/A' }}</span>
              </template>
            </Column>
            <Column field="access" header="Access">
              <template #body="{ data }">
                <div class="ml-3">
                  <Icon v-if="!!data.user && !data.archived_at" name="check" class="text-lg text-blue-600" />
                  <Icon v-else name="close" class="text-lg text-red-600" />
                </div>
              </template>
            </Column>
            <Column field="created_at" header="Date Created">
              <template #body="{ data }">
                {{ useDateTime().formatIntlDate(data.created_at) }}
              </template>
            </Column>
            <Column field="actions" header="Actions">
              <template #body="{ data }">
                <UserListActions :data="data" @on-archived="init" />
              </template>
            </Column>
          </template>
        </DataTable>
        <ImportUsersModal
          v-if="importUsersModalOpen && can([PERMISSIONS.ORGANIZATION_IMPORT_STUDENTS])"
          @cancel="onCancelAdd"
          @save="doSuccess"
          @error="doFail"
        />
        <ImportIEPModal
          v-if="importIEPModalOpen && can([PERMISSIONS.STUDENTS_IMPORT_IEP_INFORMATION])"
          @cancel="onCancelIEPAdd"
          @save="doSuccess"
          @error="doFail"
        />
        <ArchiveModal v-if="can([PERMISSIONS.PEOPLE_ARCHIVE])" />
      </template>
    </Card>
  </div>
</template>

<style lang="scss" scoped>
:deep(.p-card-body) {
  padding: 0 1rem 1rem 1rem;
  .p-datatable-header {
    background: $white;
    padding: 0.5rem;
  }
  .p-paginator-current {
    flex: 1;
    justify-content: flex-end;
  }
}

.text-red-600 {
  color: $red;
}
</style>
